import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { styled } from '@mui/material';
import validator from 'validator';

import { vcardMicrositeAPIService } from '~/api/microsites/vcard-plus';
import SocialBlock from '~/components/organisms/editor/SocialBlock/SocialBlock';
import Accordion from '~/components/ui/Accordion/Accordion';
import RadioGroup from '~/components/ui/RadioGroup';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import VCardPlusCompanies from '~/components/VCardPlusQRContent/VCardPlusContent/VCardPlusCompanies';
import VCardPlusImages from '~/components/VCardPlusQRContent/VCardPlusContent/VCardPlusImages';
import { DESKTOP_MIN_WIDTH } from '~/constants/common';
import useBuilderBlockMaxWidth from '~/hooks/useBuilderBlockMaxWidth';
import { useAppDispatch } from '~/hooks/useRedux';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { getIsDesktop } from '~/store/reducers/app/selectors';
import {
  updateVCardCompanyThunk,
  updateVCardPlusSiteThunk,
  updateVCardSocialThunk,
  vCardPlusSlice,
} from '~/store/reducers/previewScreens/vcard-plus';
import { getVCardPlusReducer } from '~/store/reducers/previewScreens/vcard-plus/selectors';
import { stepperSlice } from '~/store/reducers/stepepr';
import { getStepperData } from '~/store/reducers/stepepr/selectors';
import { COLORS } from '~/styles/colors';
import { isMaxLength } from '~/utils/common';
import { logger } from '~/utils/logger';

const { actions } = vCardPlusSlice;
const stepperActions = stepperSlice.actions;

export default function Content() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const isDesktop = useSelector(getIsDesktop);
  const vCardPlusState = useSelector(getVCardPlusReducer);
  const { isMicrositeDataValid } = useSelector(getStepperData);

  const gridSelectorMaxWidth = useBuilderBlockMaxWidth();

  const {
    title,
    micrositeData,
    content,
    content: { socialLinks, socialTitle, headerPhone, headerLocation, headerEmail, footerTitle, footerType },
    isDeleteSocialLoading,
  } = vCardPlusState;

  const mockFooterSwitch = [
    {
      label: t('template5:contactTopLabel'),
      value: 'top',
    },
    {
      label: t('template5:floatingLabel'),
      value: 'floating',
    },
  ];

  const [socialNetworks, setSocialNetworks] = useState([]);
  const [selectedFooter, setSelectedFooter] = useState(footerType);

  const areSocialsValid = socialNetworks.every((item) => validator.isURL(item.value));

  const handleSocialLinks = (val, type, index) => {
    if (micrositeData) {
      const addingValue = {
        network: val[index]?.network?.toUpperCase() || '',
        url: val[index]?.value,
        title: val[index]?.title,
        microsite: micrositeData?.id,
      };

      const areValValid = val.every((item) => validator.isURL(item.value));

      setSocialNetworks(val);
      dispatch(actions.setSocialLinks(val));

      if (areValValid) {
        switch (type) {
          case 'add':
            return dispatch(
              updateVCardSocialThunk({
                id: micrositeData?.id,
                body: addingValue,
                type: 'add',
              }),
            );
          case 'update':
            return dispatch(
              updateVCardSocialThunk({
                id: micrositeData?.id,
                body: {
                  id: micrositeData?.socials[index]?.id,
                  data: addingValue,
                },
                type: 'update',
              }),
            );
          case 'delete':
            return dispatch(
              updateVCardSocialThunk({
                id: micrositeData?.id,
                body: micrositeData?.socials[index]?.id,
                type: 'delete',
              }),
            );
          default:
            break;
        }
      }
    }
  };

  useEffect(() => {
    if (areSocialsValid && !isMicrositeDataValid && title) {
      dispatch(stepperActions.setIsMicrositeDataValid(true));
    } else {
      if (!isMicrositeDataValid) return;

      dispatch(stepperActions.setIsMicrositeDataValid(true));
    }
  }, [vCardPlusState]);

  const sendContentHeaderField = (field, id) => {
    dispatch(
      updateVCardPlusSiteThunk({
        id: micrositeData.id,
        body: {
          header: { ...micrositeData.header, [field]: content[id] },
        },
      }),
    );
  };

  const sendContentFooterField = (field, id) => {
    dispatch(
      updateVCardPlusSiteThunk({
        id: micrositeData.id,
        body: {
          [field]: content[id],
        },
      }),
    );
  };

  const sendButtonPosition = (e) => {
    if (e.target.value) {
      const payload = {
        contact_add_btn_on_the_top: e.target.value === 'top' && e.target.value !== footerType ? true : false,
        contact_add_btn_floating_button: e.target.value === 'floating' && e.target.value !== footerType ? true : false,
      };
      setSelectedFooter(e.target.value === footerType ? null : e.target.value);
      dispatch(
        updateVCardPlusSiteThunk({
          id: micrositeData.id,
          body: payload,
          dispatchHandler: () =>
            dispatch(
              actions.setContentBaseField({
                field: 'footerType',
                value: e.target.value === footerType ? null : e.target.value,
              }),
            ),
        }),
      );
    }
  };

  const updateVCardPlusData = (body) => {
    dispatch(
      updateVCardPlusSiteThunk({
        id: micrositeData.id,
        body,
      }),
    );
  };

  const createVCardPlusImage = async (body) => {
    try {
      await vcardMicrositeAPIService.uploadImage(body);
      const response = await vcardMicrositeAPIService.getVCardSite(body.microsite);
      dispatch(actions.setMicrositeData(response.data));
    } catch (e) {
      logger.log('createVCardPlusImage', e);
    }
  };
  const deleteVCardPlusImage = async (id) => {
    await vcardMicrositeAPIService.removeImage(id);
    const response = await vcardMicrositeAPIService.getVCardSite(micrositeData?.id);
    dispatch(actions.setMicrositeData(response.data));
  };
  const createVCardPlusCompany = (data) => {
    dispatch(
      updateVCardCompanyThunk({
        type: 'add',
        id: data.microsite,
        body: data,
      }),
    );
  };
  const updateVCardPlusCompany = ({ body, companyId }) => {
    dispatch(
      updateVCardCompanyThunk({
        type: 'update',
        id: micrositeData?.id,
        body: {
          id: companyId,
          data: body,
        },
      }),
    );

    void vcardMicrositeAPIService.updateVCardCompany(body, companyId);
  };
  const deleteVCardPlusCompany = (companyId) => {
    dispatch(
      updateVCardCompanyThunk({
        id: micrositeData?.id,
        body: companyId,
      }),
    );
  };

  return (
    <CreateQRContentBlockLayout type="content">
      <VCardPlusImages
        gridSelectorMaxWidth={gridSelectorMaxWidth}
        updateVCardPlusData={updateVCardPlusData}
        vCardPlusData={micrositeData}
        createVCardPlusImage={createVCardPlusImage}
        deleteVCardPlusImage={deleteVCardPlusImage}
      />
      <VCardPlusCompanies
        createVCardPlusCompany={createVCardPlusCompany}
        vCardPlusData={micrositeData}
        deleteVCardPlusCompany={deleteVCardPlusCompany}
        updateVCardPlusCompany={updateVCardPlusCompany}
      />
      <Accordion
        defaultExpanded
        variant="child"
        summary={t('template5:custBtnText')}
        details={
          <>
            <StyledButtonsBlock>
              <Text variant="buttonSmallMedium">{t('template5:header')}</Text>
              <StyledButtonsHeader>
                <TextInput
                  defaultValue={headerPhone}
                  onChange={(e) =>
                    dispatch(actions.setContentBaseField({ field: 'headerPhone', value: e.target.value }))
                  }
                  onBlur={() => sendContentHeaderField('header_phone', 'headerPhone')}
                  sx={{ flex: 1 }}
                  fullWidth
                  helperText={isMaxLength(headerPhone, 15)}
                  inputProps={{ maxLength: 15 }}
                  label={t('other:phone')}
                  placeholder={t('template2:writeHere')}
                />
                <TextInput
                  defaultValue={headerLocation}
                  onChange={(e) =>
                    dispatch(actions.setContentBaseField({ field: 'headerLocation', value: e.target.value }))
                  }
                  onBlur={() => sendContentHeaderField('header_location', 'headerLocation')}
                  sx={{ flex: 1 }}
                  fullWidth
                  helperText={isMaxLength(headerLocation, 15)}
                  inputProps={{ maxLength: 15 }}
                  label={t('other:location')}
                  placeholder={t('template2:writeHere')}
                />
                <TextInput
                  defaultValue={headerEmail}
                  onChange={(e) =>
                    dispatch(actions.setContentBaseField({ field: 'headerEmail', value: e.target.value }))
                  }
                  onBlur={() => sendContentHeaderField('header_email', 'headerEmail')}
                  sx={{ flex: 1 }}
                  fullWidth
                  helperText={isMaxLength(headerEmail, 15)}
                  inputProps={{ maxLength: 15 }}
                  label={t('other:email')}
                  placeholder={t('template2:writeHere')}
                />
              </StyledButtonsHeader>
            </StyledButtonsBlock>

            <StyledButtonsBlock>
              <Text variant="buttonSmallMedium">{t('template5:footer')}</Text>
              <TextInput
                defaultValue={footerTitle}
                helperText={isMaxLength(footerTitle, 15)}
                inputProps={{ maxLength: 30 }}
                onChange={(e) => dispatch(actions.setContentBaseField({ field: 'footerTitle', value: e.target.value }))}
                onBlur={() => sendContentFooterField('footer', 'footerTitle')}
                sx={{ flex: 1 }}
                fullWidth
                disabled={selectedFooter === 'floating'}
                label={t('other:title')}
                placeholder={t('template2:writeHere')}
              />
            </StyledButtonsBlock>
            <RadioGroup
              value={selectedFooter}
              sx={{ marginTop: '12px', gap: isDesktop ? '60px' : '12px', flexDirection: isDesktop ? 'row' : 'column' }}
              radios={mockFooterSwitch}
              onClick={sendButtonPosition}
            />
          </>
        }
      />
      <SocialBlock
        dispatchHandler={(val) => dispatch(actions.setSocialLinks(val))}
        setSocialLinks={handleSocialLinks}
        socialLinks={socialLinks}
        renderLabel={false}
        renderTextField={() => (
          <TextInput
            defaultValue={socialTitle}
            helperText={isMaxLength(socialTitle, 100)}
            inputProps={{ maxLength: 100 }}
            onChange={(e) => dispatch(actions.setContentBaseField({ field: 'socialTitle', value: e.target.value }))}
            onBlur={() => sendContentFooterField('socials_title', 'socialTitle')}
            sx={{ marginBottom: '16px' }}
            fullWidth
            label={t('other:title')}
            placeholder={t('template2:writeHere')}
          />
        )}
        isDeleteDisabled={isDeleteSocialLoading}
      />
    </CreateQRContentBlockLayout>
  );
}

export const StyledButtonsBlock = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
  borderRadius: '20px',
  padding: '12px',
  backgroundColor: COLORS.lightGray,
  '&:first-of-type': {
    marginBottom: '12px',
  },
}));

export const StyledButtonsHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: '16px',
  [theme.breakpoints.down(DESKTOP_MIN_WIDTH)]: {
    flexDirection: 'column',
  },
}));
